import { useCallback, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { selectCurrentProject } from '@features/projects';
import { startNewConversation } from '@features/ai';

import { selectSidebarCollapsed, selectSidebarWidth, setSidebarWidth, toggleSidebar } from '../store/workspaceSlice';
import { ConversationList } from './ConversationList';
import { FileTree } from './FileTree';

function SidebarSection({ icon, label, children, defaultOpen = true, grow = false, action = null, dataTour, className = '' }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <section
      data-tour={dataTour}
      // Open growing sections share the sidebar height evenly (flex-1 = basis 0);
      // a collapsed one shrinks to its header and hands its half to the other.
      className={`flex flex-col min-h-0 ${grow && open ? 'flex-1' : 'shrink-0'} ${className}`}
    >
      <div className="flex w-full shrink-0 items-center gap-2 px-4 py-2.5">
        <Icon icon={icon} className="size-4 text-[var(--color-accent)]" />
        <span className="flex-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-text-secondary)]">{label}</span>
        {action}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={t(open ? 'workspace.sidebar.collapseSection' : 'workspace.sidebar.expandSection', { section: label })}
          className="flex size-6 cursor-pointer items-center justify-center rounded-md text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-text-secondary)]"
        >
          <Icon icon="mdi:chevron-down" className={`size-4 transition-transform ${open ? '' : '-rotate-90'}`} />
        </button>
      </div>
      {open && (
        <div id={panelId} className={grow ? 'flex-1 min-h-0 overflow-y-auto px-2 pb-2' : 'max-h-56 overflow-y-auto px-2 pb-2'}>{children}</div>
      )}
    </section>
  );
}

SidebarSection.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultOpen: PropTypes.bool,
  grow: PropTypes.bool,
  action: PropTypes.node,
  dataTour: PropTypes.string,
  className: PropTypes.string,
};

const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

export function WorkspaceSidebar() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const collapsed = useAppSelector(selectSidebarCollapsed);
  const sidebarWidth = useAppSelector(selectSidebarWidth);
  const currentProject = useAppSelector(selectCurrentProject);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + moveEvent.clientX - startX));
      dispatch(setSidebarWidth(newWidth));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth, dispatch]);

  if (collapsed) {
    return (
      <aside className="flex h-full w-12 shrink-0 flex-col items-center gap-2 border-r border-[var(--color-border)] bg-white py-3">
        <button
          type="button"
          onClick={() => { dispatch(startNewConversation()); navigate('/chat'); }}
          disabled={!currentProject}
          title={t('workspace.sidebar.newConversation')}
          aria-label={t('workspace.sidebar.newConversation')}
          className="cursor-pointer flex size-8 items-center justify-center rounded-lg text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-accent)] disabled:cursor-not-allowed disabled:text-[var(--color-border-strong)]"
        >
          <Icon icon="mdi:plus" className="size-4" />
        </button>
        <button type="button" onClick={() => navigate('/chat')} title={currentProject ? t('workspace.sidebar.projectConversations', { project: currentProject.name }) : t('workspace.sidebar.conversations')} aria-label={t('workspace.sidebar.conversations')} className="cursor-pointer flex size-8 items-center justify-center rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-accent-subtle)] hover:text-[var(--color-accent)]">
          <Icon icon="mdi:message-text-outline" className="size-4" />
        </button>
        <button type="button" onClick={() => dispatch(toggleSidebar())} title={t('workspace.sidebar.openDataroom')} aria-label={t('workspace.sidebar.openDataroom')} className="cursor-pointer flex size-8 items-center justify-center rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-accent-subtle)] hover:text-[var(--color-accent)]">
          <Icon icon="mdi:folder-outline" className="size-4" />
        </button>
      </aside>
    );
  }

  return (
    <aside
      ref={sidebarRef}
      style={{ width: sidebarWidth }}
      className="relative flex h-full shrink-0 flex-col overflow-hidden border-r border-[var(--color-border)] bg-white"
    >
      {/* Resize handle */}
      <div
        onMouseDown={handleMouseDown}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            const delta = event.key === 'ArrowRight' ? 16 : -16;
            dispatch(setSidebarWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, sidebarWidth + delta))));
          }
        }}
        role="separator"
        aria-orientation="vertical"
        aria-label={t('workspace.sidebar.resize')}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={MAX_WIDTH}
        aria-valuenow={sidebarWidth}
        tabIndex={0}
        className={`absolute top-0 right-0 z-10 h-full w-1 cursor-col-resize transition-colors hover:bg-[var(--color-accent)] focus:w-1.5 focus:bg-[var(--color-accent)] focus:outline-none ${isResizing ? 'bg-[var(--color-accent)]' : ''}`}
      />
      {/* Scrollable middle area */}
      <div className="flex min-h-0 flex-1 flex-col">
        {currentProject ? (
          <>
            <SidebarSection
              icon="mdi:message-text-outline"
              label={t('workspace.sidebar.conversations')}
              dataTour="project-conversations"
              grow
              action={(
                <button
                  type="button"
                  onClick={() => { dispatch(startNewConversation()); navigate('/chat'); }}
                  data-tour="new-chat"
                  title={t('workspace.sidebar.startNewConversation')}
                  aria-label={t('workspace.sidebar.startNewConversation')}
                  className="flex size-6 cursor-pointer items-center justify-center rounded-md text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-muted)] hover:text-[var(--color-accent)]"
                >
                  <Icon icon="mdi:plus" className="size-4" />
                </button>
              )}
            >
              <ConversationList />
            </SidebarSection>

            <SidebarSection
              icon="mdi:folder-outline"
              label={t('workspace.sidebar.dataroom')}
              dataTour="project-files"
              grow
              className="border-t border-[var(--color-border)]"
            >
              <FileTree key={currentProject.uuid} />
            </SidebarSection>
          </>
        ) : <div className="flex-1" />}
      </div>
    </aside>
  );
}
